import { Link } from "react-router-dom";
import { WhipLogo } from "@/components/WhipLogo";
import { Eye, Users, Crosshair, Zap, ArrowRight } from "lucide-react";
import { EarlyAccessForm } from "@/components/EarlyAccessForm";

const FEATURES = [
  {
    icon: Eye,
    title: "See Ahead",
    blurb:
      "Know what's likely to move, and what isn't, before the rest of town does.",
  },
  {
    icon: Users,
    title: "Know the Players",
    blurb:
      "Understand who actually delivers, who's rising, and where everyone really stands.",
  },
  {
    icon: Crosshair,
    title: "Cut the Noise",
    blurb:
      "Intelligence shaped around what you care about, not a firehose of everything.",
  },
  {
    icon: Zap,
    title: "Never Miss a Shift",
    blurb:
      "When the landscape changes, you see it immediately, not in next week's newsletter.",
  },
];

export default function About() {
  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg, #0b0b0f)" }}>
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <WhipLogo size={26} />
          <Link
            to="/login"
            className="rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors"
            data-testid="about-signin-cta-top"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <section className="py-20 lg:py-28 max-w-3xl whip-hero-fade">
          <p className="text-[11px] font-mono font-bold uppercase tracking-[0.3em] text-orange-400 mb-5">
            Legislative Intelligence Platform
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
            Know what Congress will do
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-400"> before it does it.</span>
          </h1>
          <p className="text-base text-white/60 mt-6 leading-relaxed max-w-2xl">
            WHIP turns the chaos of Congress into one clear command center, who matters,
            what's moving, and what it means for you. Less reading, better decisions,
            earlier than everyone else.
          </p>
          <div className="flex items-center gap-4 mt-9">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-7 py-3 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors"
              data-testid="about-signin-cta-hero"
            >
              Sign in to WHIP <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="text-[11px] font-mono uppercase tracking-wider text-white/35">
              Invite-only access
            </span>
          </div>
        </section>

        {/* Stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-xl overflow-hidden border border-white/10 mb-20" data-testid="about-stats-strip">
          {[
            ["538", "Members tracked"],
            ["10K+", "Bills analyzed"],
            ["Live", "Real-time updates"],
            ["24/7", "Always watching"],
          ].map(([big, small]) => (
            <div key={small} className="bg-white/[0.02] px-6 py-5">
              <p className="text-2xl font-black font-mono text-orange-400">{big}</p>
              <p className="text-[11px] font-mono uppercase tracking-wider text-white/40 mt-1">{small}</p>
            </div>
          ))}
        </section>

        {/* Features */}
        <section className="pb-24">
          <h2 className="text-lg font-bold text-white mb-8">Why teams use WHIP</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-features">
            {FEATURES.map(({ icon: Icon, title, blurb }) => (
              <div key={title} className="whip-surface rounded-xl border border-white/10 p-6 hover:border-orange-500/30 transition-colors">
                <Icon className="w-5 h-5 text-orange-400 mb-4" />
                <h3 className="text-white font-bold mb-2">{title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{blurb}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Early access */}
        <section className="pb-24" id="early-access" data-testid="about-early-access">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-black text-white tracking-tight mb-2">Request Early Access</h2>
            <p className="text-sm text-white/55 mb-6">
              WHIP is currently invite-only. Tell us who you are and how you'd use it, we review every request.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <EarlyAccessForm />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="pb-24 text-center">
          <div className="whip-surface rounded-2xl border border-orange-500/25 px-8 py-14">
            <h2 className="text-lg font-bold text-white">Ready to see the whole board?</h2>
            <p className="text-sm text-white/55 mt-2 mb-7">Sign in with your invited account to open the full platform.</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-7 py-3 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors"
              data-testid="about-signin-cta-bottom"
            >
              Sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-6">
        <p className="text-center text-[11px] font-mono text-white/30">
          WHIP · Built on official public congressional records · Insights are analytical estimates, not guarantees
          {" · "}
          <Link to="/privacy" className="text-white/45 hover:text-orange-400 underline underline-offset-2" data-testid="about-privacy-link">
            Privacy
          </Link>
        </p>
      </footer>
    </div>
  );
}
